import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
    buildTippingCardFilename,
    canvasToBlob,
    createTippingCardBlob,
    downloadBlob,
    getTippingCardHeadline,
    renderTippingCard,
} from "../lib/tippingCard";

const DownloadIcon = () => (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 3v12" />
        <path d="m7 10 5 5 5-5" />
        <path d="M5 21h14" />
    </svg>
);

const CloseIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
        <path d="M6 6l12 12M18 6 6 18" />
    </svg>
);

export default function TippingCardDownloadModal({
    open,
    onClose,
    tipLink,
    targetName,
    isTeam = false,
}) {
    const [canvas, setCanvas] = useState(null);
    const [previewUrl, setPreviewUrl] = useState("");
    const [error, setError] = useState("");
    const [downloading, setDownloading] = useState("");
    const [generationKey, setGenerationKey] = useState(0);
    const dialogRef = useRef(null);
    const closeButtonRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const previouslyFocused = document.activeElement;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        closeButtonRef.current?.focus();

        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                event.preventDefault();
                onClose();
                return;
            }

            if (event.key !== "Tab" || !dialogRef.current) return;
            const focusable = Array.from(dialogRef.current.querySelectorAll("button:not([disabled]), a[href]"));
            if (!focusable.length) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
            document.body.style.overflow = previousOverflow;
            previouslyFocused?.focus?.();
        };
    }, [open, onClose]);

    useEffect(() => {
        if (!open) return undefined;
        let cancelled = false;
        let nextPreviewUrl = "";

        setCanvas(null);
        setPreviewUrl("");
        setError("");

        const prepareCard = async () => {
            try {
                const nextCanvas = await renderTippingCard({ tipLink, targetName, isTeam });
                const previewBlob = await canvasToBlob(nextCanvas, "image/png");
                nextPreviewUrl = URL.createObjectURL(previewBlob);
                if (cancelled) {
                    URL.revokeObjectURL(nextPreviewUrl);
                    return;
                }
                setCanvas(nextCanvas);
                setPreviewUrl(nextPreviewUrl);
            } catch (generationError) {
                if (!cancelled) {
                    console.error("Failed to create the Tiply tipping card:", generationError);
                    setError("We couldn’t create the card. Please try again.");
                }
            }
        };

        prepareCard();
        return () => {
            cancelled = true;
            if (nextPreviewUrl) URL.revokeObjectURL(nextPreviewUrl);
        };
    }, [open, tipLink, targetName, isTeam, generationKey]);

    if (!open) return null;

    const headline = getTippingCardHeadline({ targetName, isTeam });
    const handleDownload = async (extension) => {
        if (!canvas || downloading) return;
        setDownloading(extension);
        setError("");
        try {
            const blob = await createTippingCardBlob(canvas, extension);
            downloadBlob(blob, buildTippingCardFilename({ targetName, isTeam, extension }));
        } catch (downloadError) {
            console.error(`Failed to download the ${extension.toUpperCase()} tipping card:`, downloadError);
            setError("The download could not be prepared. Please try again.");
        } finally {
            setDownloading("");
        }
    };

    const modal = (
        <div
            className="tipping-card-modal-backdrop"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) onClose();
            }}
        >
            <style>{`
                .tipping-card-modal-backdrop {
                    position: fixed;
                    inset: 0;
                    z-index: 1000;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 24px;
                    background: rgba(20, 20, 35, 0.68);
                    backdrop-filter: blur(5px);
                }
                .tipping-card-modal {
                    width: min(880px, 100%);
                    max-height: calc(100vh - 48px);
                    overflow: auto;
                    border-radius: 22px;
                    background: #fff;
                    color: #1a1a2e;
                    box-shadow: 0 28px 80px rgba(0, 0, 0, 0.28);
                    font-family: 'Urbanist', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                }
                .tipping-card-modal-header {
                    display: flex;
                    align-items: flex-start;
                    justify-content: space-between;
                    gap: 20px;
                    padding: 24px 26px 20px;
                    border-bottom: 1px solid #e8e8ee;
                }
                .tipping-card-modal-header h2 {
                    margin: 0;
                    font-size: 22px;
                    line-height: 1.2;
                }
                .tipping-card-modal-header p {
                    margin: 6px 0 0;
                    color: #6b6b82;
                    font-size: 14px;
                }
                .tipping-card-modal-close {
                    display: inline-flex;
                    width: 38px;
                    height: 38px;
                    flex: 0 0 auto;
                    align-items: center;
                    justify-content: center;
                    border: 1px solid #e0e0e8;
                    border-radius: 10px;
                    background: #fff;
                    color: #55556b;
                    cursor: pointer;
                }
                .tipping-card-modal-close:hover { background: #f5f5f8; color: #1a1a2e; }
                .tipping-card-modal-content {
                    display: grid;
                    grid-template-columns: minmax(250px, 330px) minmax(260px, 1fr);
                    gap: 36px;
                    align-items: center;
                    padding: 28px;
                }
                .tipping-card-preview-shell {
                    display: flex;
                    min-height: 460px;
                    align-items: center;
                    justify-content: center;
                    border: 1px solid #e3e3ea;
                    border-radius: 18px;
                    background: #f3f3f7;
                    padding: 18px;
                }
                .tipping-card-preview {
                    display: block;
                    width: auto;
                    max-width: 100%;
                    height: auto;
                    max-height: 520px;
                    aspect-ratio: 7 / 12;
                    background: #fff;
                    box-shadow: 0 12px 34px rgba(18, 18, 32, 0.14);
                }
                .tipping-card-loading {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 12px;
                    color: #6b6b82;
                    font-size: 14px;
                    text-align: center;
                }
                .tipping-card-spinner {
                    width: 30px;
                    height: 30px;
                    border: 3px solid #c9f7ed;
                    border-top-color: #0d7c5f;
                    border-radius: 50%;
                    animation: tipping-card-spin 0.75s linear infinite;
                }
                @keyframes tipping-card-spin { to { transform: rotate(360deg); } }
                .tipping-card-modal-actions h3 {
                    margin: 0 0 8px;
                    font-size: 20px;
                }
                .tipping-card-modal-actions > p {
                    margin: 0 0 22px;
                    color: #6b6b82;
                    font-size: 14px;
                    line-height: 1.55;
                }
                .tipping-card-size-note {
                    display: flex;
                    gap: 10px;
                    align-items: flex-start;
                    margin-bottom: 22px;
                    padding: 13px 14px;
                    border-radius: 12px;
                    background: #eaf9f4;
                    color: #17644f;
                    font-size: 13px;
                    line-height: 1.4;
                }
                .tipping-card-size-note strong { display: block; margin-bottom: 2px; }
                .tipping-card-downloads { display: grid; gap: 10px; }
                .tipping-card-download {
                    display: inline-flex;
                    width: 100%;
                    min-height: 46px;
                    align-items: center;
                    justify-content: center;
                    gap: 9px;
                    border: 1px solid #cfd0da;
                    border-radius: 11px;
                    background: #fff;
                    color: #1a1a2e;
                    font: 700 14px 'Urbanist', sans-serif;
                    cursor: pointer;
                }
                .tipping-card-download.primary {
                    border-color: #0d7c5f;
                    background: #0d7c5f;
                    color: #fff;
                }
                .tipping-card-download:hover:not(:disabled) { transform: translateY(-1px); }
                .tipping-card-download.primary:hover:not(:disabled) { background: #09674f; }
                .tipping-card-download:disabled { cursor: wait; opacity: 0.55; }
                .tipping-card-custom-design {
                    margin: 18px 0 0;
                    padding-top: 17px;
                    border-top: 1px solid #e8e8ee;
                    color: #6b6b82;
                    font-size: 13px;
                    line-height: 1.5;
                }
                .tipping-card-custom-design a {
                    color: #0d7c5f;
                    font-weight: 700;
                    text-decoration: none;
                }
                .tipping-card-custom-design a:hover { text-decoration: underline; }
                .tipping-card-error {
                    margin: 14px 0 0;
                    border-radius: 10px;
                    background: #fef2f2;
                    color: #b91c1c;
                    padding: 11px 12px;
                    font-size: 13px;
                }
                .tipping-card-retry {
                    margin-top: 8px;
                    border: 0;
                    background: transparent;
                    color: #8d1111;
                    font: inherit;
                    font-weight: 700;
                    text-decoration: underline;
                    cursor: pointer;
                }
                @media (max-width: 700px) {
                    .tipping-card-modal-backdrop { align-items: flex-start; padding: 12px; }
                    .tipping-card-modal { max-height: calc(100vh - 24px); border-radius: 17px; }
                    .tipping-card-modal-header { padding: 18px; }
                    .tipping-card-modal-content { grid-template-columns: 1fr; gap: 24px; padding: 18px; }
                    .tipping-card-preview-shell { min-height: 0; }
                    .tipping-card-preview { max-height: 52vh; }
                }
                @media (prefers-reduced-motion: reduce) {
                    .tipping-card-download:hover:not(:disabled) { transform: none; }
                }
            `}</style>

            <section
                ref={dialogRef}
                className="tipping-card-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="tipping-card-modal-title"
                aria-describedby="tipping-card-modal-description"
            >
                <header className="tipping-card-modal-header">
                    <div>
                        <h2 id="tipping-card-modal-title">Ready-to-print tipping card</h2>
                        <p id="tipping-card-modal-description">Preview and download your card for printing.</p>
                    </div>
                    <button ref={closeButtonRef} className="tipping-card-modal-close" type="button" onClick={onClose} aria-label="Close ready-to-print tipping card">
                        <CloseIcon />
                    </button>
                </header>

                <div className="tipping-card-modal-content">
                    <div className="tipping-card-preview-shell">
                        {previewUrl ? (
                            <img className="tipping-card-preview" src={previewUrl} alt={`Preview of the “${headline}” Tiply tipping card`} />
                        ) : (
                            <div className="tipping-card-loading" role="status">
                                <span className="tipping-card-spinner" aria-hidden="true" />
                                {error ? "Card preview unavailable" : "Preparing your card…"}
                            </div>
                        )}
                    </div>

                    <div className="tipping-card-modal-actions">
                        <h3>Ready to print</h3>
                        <p>PDF keeps the correct physical size and is the best option for a print shop or office printer.</p>
                        <div className="tipping-card-size-note">
                            <span aria-hidden="true">✦</span>
                            <span><strong>Finished size: 50 × 90 mm</strong>3 mm bleed is already included on every edge.</span>
                        </div>
                        <div className="tipping-card-downloads">
                            <button className="tipping-card-download primary" type="button" disabled={!canvas || Boolean(downloading)} onClick={() => handleDownload("pdf")}>
                                <DownloadIcon /> {downloading === "pdf" ? "Preparing PDF…" : "Download PDF"}
                            </button>
                            <button className="tipping-card-download" type="button" disabled={!canvas || Boolean(downloading)} onClick={() => handleDownload("png")}>
                                <DownloadIcon /> {downloading === "png" ? "Preparing PNG…" : "Download PNG"}
                            </button>
                            <button className="tipping-card-download" type="button" disabled={!canvas || Boolean(downloading)} onClick={() => handleDownload("jpeg")}>
                                <DownloadIcon /> {downloading === "jpeg" ? "Preparing JPEG…" : "Download JPEG"}
                            </button>
                        </div>
                        <p className="tipping-card-custom-design">
                            Want a custom design? We can help—contact us at <a href="mailto:team@tiply.ee">team@tiply.ee</a>.
                        </p>
                        {error && (
                            <div className="tipping-card-error" role="alert">
                                {error}
                                {!canvas && (
                                    <button className="tipping-card-retry" type="button" onClick={() => setGenerationKey((key) => key + 1)}>Try again</button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );

    return createPortal(modal, document.body);
}
